// src/components/shops/postTemplates/Stamp.tsx
//
// A postmark struck at the edge of the photograph, for the posts a shop only
// makes once — an anniversary, a millionth cup, a new site.
//
// Customers already know the idea from the passport stamps on their coffee map,
// so it reads as a milestone immediately. The mark straddles the photo edge
// rather than sitting on top of the picture: struck over a photograph it would
// be legible on a dark one and invisible on a bright one.

import { cachedUrl } from '../../../lib/storageUrl'
import SocialBrewBadge from '../SocialBrewBadge'
import { type TemplateProps } from './types'

const INK = '#A73127'

function stampDate(createdAt?: string | null) {
  const d = createdAt ? new Date(createdAt) : new Date()
  if (Number.isNaN(d.getTime())) return ''
  return d
    .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .toUpperCase()
}

export default function Stamp({
  title, body, photos, createdAt, shopName, rating, ratingCount, scale = 1,
}: TemplateProps) {
  const px = (n: number) => `${n * scale}px`
  const photo = photos[0]
  const size = 104 * scale

  return (
    <div
      className="overflow-hidden"
      style={{ background: '#f7efe1', borderRadius: px(4) }}
      data-shop={shopName}
    >
      {photo && (
        <div style={{ aspectRatio: '4 / 5' }}>
          <img src={cachedUrl(photo)} alt="" loading="lazy" className="w-full h-full object-cover" />
        </div>
      )}

      <div style={{ padding: `0 ${px(16)} ${px(14)}`, position: 'relative' }}>
        {/* The mark, pulled up so it breaks the photo edge */}
        <div
          style={{
            width: size,
            height: size,
            marginTop: photo ? -size / 2 : px(18),
            marginLeft: 'auto',
            marginRight: px(2),
            borderRadius: '50%',
            background: '#f7efe1',
            border: `${px(2.5)} solid ${INK}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'rotate(-8deg)',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: px(5),
              borderRadius: '50%',
              border: `${px(1)} dashed ${INK}`,
              opacity: 0.55,
            }}
          />
          <p
            style={{
              fontSize: px(7.5),
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: INK,
              fontWeight: 700,
              margin: 0,
              maxWidth: px(78),
              textAlign: 'center',
              lineHeight: 1.25,
            }}
          >
            {shopName}
          </p>
          <div style={{ width: px(34), borderTop: `${px(1)} solid ${INK}`, margin: `${px(5)} 0`, opacity: 0.7 }} />
          <p style={{ fontSize: px(7.5), letterSpacing: '0.14em', color: INK, fontWeight: 700, margin: 0 }}>
            {stampDate(createdAt)}
          </p>
        </div>

        <div style={{ marginTop: photo ? px(-14) : px(14) }}>
          <h3
            className="font-display"
            style={{
              fontSize: px(25),
              lineHeight: 1.1,
              letterSpacing: '-0.015em',
              color: '#241810',
              fontWeight: 700,
              margin: 0,
              // Keeps the headline clear of the mark hanging above it.
              maxWidth: '72%',
              textWrap: 'balance',
            }}
          >
            {title}
          </h3>
          {body && (
            <p style={{ fontSize: px(13), lineHeight: 1.62, color: '#6e5038', marginTop: px(8) }}>
              {body}
            </p>
          )}
        </div>

        <div style={{ marginTop: px(16) }}>
          <SocialBrewBadge rating={rating} ratingCount={ratingCount} tone="light" scale={scale} />
        </div>
      </div>
    </div>
  )
}
